import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { Plus, Trash2, Edit2, Copy, MessageSquare, Mail, X, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessageTemplates() {
  const { isDark } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', channel: 'linkedin', type: 'direct_message', subject: '', body: '', merge_tags: [] });
  const [mergeTags, setMergeTags] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTemplates(); fetchMergeTags(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/outreach/templates`, { credentials: 'include' });
      if (res.ok) setTemplates(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchMergeTags = async () => {
    const res = await fetch(`${BACKEND_URL}/api/prospecting/outreach/merge-tags`, { credentials: 'include' });
    if (res.ok) setMergeTags(await res.json());
  };

  const handleSave = async () => {
    const url = editingId ? `${BACKEND_URL}/api/prospecting/outreach/templates/${editingId}` : `${BACKEND_URL}/api/prospecting/outreach/templates`;
    const method = editingId ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    if (res.ok) { fetchTemplates(); setShowForm(false); setEditingId(null); resetForm(); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await fetch(`${BACKEND_URL}/api/prospecting/outreach/templates/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchTemplates();
  };

  const startEdit = (t) => {
    setForm({ name: t.name, channel: t.channel, type: t.type, subject: t.subject || '', body: t.body, merge_tags: t.merge_tags || [] });
    setEditingId(t.template_id);
    setShowForm(true);
  };

  const resetForm = () => setForm({ name: '', channel: 'linkedin', type: 'direct_message', subject: '', body: '', merge_tags: [] });

  const insertTag = (tag) => setForm(prev => ({ ...prev, body: prev.body + tag }));

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;
  const input = `w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} border`;

  return (
    <AppLayout title="Message Templates">
      <div className="space-y-6" data-testid="message-templates">
        <ProspectingNav />
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Message Templates</h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Reusable message templates for outreach</p>
          </div>
          <button onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }} data-testid="create-template-btn" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] text-sm">
            <Plus className="w-4 h-4" /> New Template
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className={`w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl p-6 ${isDark ? 'bg-[#111] border border-white/10' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editingId ? 'Edit' : 'New'} Template</h2>
                <button onClick={() => { setShowForm(false); setEditingId(null); }} className={`p-1 rounded ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Template name *" className={input} data-testid="template-name" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.channel} onChange={e => setForm(p => ({ ...p, channel: e.target.value }))} className={input}>
                    <option value="linkedin">LinkedIn</option>
                    <option value="email">Email</option>
                  </select>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className={input}>
                    <option value="connection_request">Connection Request</option>
                    <option value="direct_message">Direct Message</option>
                    <option value="email">Email</option>
                  </select>
                </div>
                {form.channel === 'email' && (
                  <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Email subject" className={input} />
                )}
                <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} placeholder="Message body..." rows={6} className={`${input} resize-none`} data-testid="template-body" />
                <div>
                  <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Insert merge tag:</p>
                  <div className="flex flex-wrap gap-1">
                    {mergeTags.map(tag => (
                      <button key={tag.tag} type="button" onClick={() => insertTag(tag.tag)} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-700'}`} title={tag.description}>
                        {tag.tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button onClick={() => { setShowForm(false); setEditingId(null); }} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button onClick={handleSave} data-testid="save-template-btn" className="px-4 py-2 rounded-lg text-sm bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600]">
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Templates List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00]"></div></div>
        ) : templates.length === 0 ? (
          <div className={`${card} p-12 text-center`}>
            <MessageSquare className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No templates yet. Create your first message template.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {templates.map(t => (
              <div key={t.template_id} className={`${card} p-4`} data-testid={`tmpl-${t.template_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {t.channel === 'email' ? <Mail className="w-4 h-4 text-blue-400" /> : <MessageSquare className="w-4 h-4 text-purple-400" />}
                      <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs capitalize ${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{t.type.replace('_', ' ')}</span>
                    </div>
                    <p className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.body}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(t)} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(t.template_id)} className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
